<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import EchoButton from '../../EchoButton.vue';
    import FavoriteFilters from '../../filter/FavoriteFilters.vue';
    import AllFilters from '../../filter/AllFilters.vue';
    import { ref } from 'vue';
    import InteractionKeywordSearch from './InteractionKeywordSearch.vue';
    import { useScheduleCoaching } from '@/js/stores/scheduleCoaching';
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import ActiveFiltersContainer from '../../filter/ActiveFiltersContainer.vue';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, QualityAssuranceModulePermissionFlag } from '@/js/consts/permissions';
    import { useFilterWatch } from '@/js/composables/useFilterWatch';
    import { ClockIcon } from '@lucide/vue';

    const { hasPermission } = usePermissions();

    const canScheduleCoaching = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.ScheduleCoaching
    );

    const allFiltersVisible = ref(false);
    const showAddFavoriteModal = ref(false);

    useFilterWatch(FilterStoreName.SharedFilter);
    const scheduleCoachingStore = useScheduleCoaching();
</script>
<template>
    <div class="flex items-stretch">
        <div class="w-full pt-10 overflow-x-auto custom-scrollbar">
            <div class="">
                <div class="flex w-full justify-between px-7.5">
                    <InteractionKeywordSearch />
                    <div class="flex h-12" :class="allFiltersVisible ? '' : 'mr-35'">
                        <EchoButton
                            v-if="canScheduleCoaching"
                            :aria-label="__('interactions-schedule-coaching')"
                            :title="__('interactions-schedule-coaching')"
                            @click="scheduleCoachingStore.openScheduleCoaching"
                        >
                            {{ __('interactions-schedule-coaching') }}
                            <template #icon-right><ClockIcon /></template>
                        </EchoButton>
                    </div>
                </div>
                <div class="flex">
                    <div class="flex flex-grow min-w-0 pt-8">
                        <ActiveFiltersContainer
                            :filter-store-name="FilterStoreName.SharedFilter"
                            match-table-padding
                        >
                            <template #save-filter>
                                <EchoButton
                                    variant="primary"
                                    size="small"
                                    :title="__('favorite-filters-save')"
                                    @click="showAddFavoriteModal = true"
                                >
                                    {{ __('favorite-filters-save') }}
                                </EchoButton>
                            </template>
                            <template #favorite-filters>
                                <FavoriteFilters
                                    v-model:show-add-modal="showAddFavoriteModal"
                                    :filter-store-name="FilterStoreName.SharedFilter"
                                />
                            </template>
                        </ActiveFiltersContainer>
                    </div>
                </div>
            </div>
            <div class="px-7.5 pb-7.5">
                <slot name="table" />
            </div>
        </div>
        <div class="relative">
            <div class="top-0" :class="allFiltersVisible ? 'sticky w-80' : ''">
                <AllFilters
                    :filter-store-name="FilterStoreName.SharedFilter"
                    :is-visible="allFiltersVisible"
                    match-table-padding
                    @toggle-visibility="(visible) => (allFiltersVisible = visible)"
                />
            </div>
        </div>
    </div>
</template>
